import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Table } from 'react-bootstrap';
import { OpenAPI } from '../api/core/OpenAPI';
import { request as __request } from '../api/core/request';
import '../../sass/pages/p2p.scss';

interface P2PSession {
  id?: number;
  email: string;
  p2pFlag: boolean;
  environment: string;
  apiUserName: string;
  apiEncryptKey: string;
  sessionId: string;
  userType: string;
  displayName: string;
  appointmentTime: string;
  userId: string;
  csn: string;
  deviceId: string; // DepartmentId is stored here
  poh: boolean;
  sessionName: string;
  updated: number;
}

interface P2PEvent {
  id: number;
  environment: string;
  eventTime: string;
  eventType: string;
  sessionId: string;
  userType: string;
  userId: string;
  customer: string;
  updated: number;
}

const P2PTesting: React.FC = () => {
  const [environment, setEnvironment] = useState('env3');
  const [apiUserName, setApiUserName] = useState('');
  const [apiEncryptKey, setApiEncryptKey] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [userType, setUserType] = useState('1');
  const [displayName, setDisplayName] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [userId, setUserId] = useState('');
  const [csn, setCsn] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  
  const [generatedLink, setGeneratedLink] = useState('');
  const [generatedSoFLink, setGeneratedSoFLink] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState<'success' | 'error' | 'info'>('info');
  
  const [sessions, setSessions] = useState<string[]>([]);
  const [sessionName, setSessionName] = useState('');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [events, setEvents] = useState<P2PEvent[]>([]);
  const [maxListSize, setMaxListSize] = useState(50);

  const environments = [
    { value: 'stage', label: 'Staging' },
    { value: 'prod', label: 'Production' },
    { value: 'env1', label: 'ENV1' },
    { value: 'env2', label: 'ENV2' },
    { value: 'env3', label: 'ENV3' },
    { value: 'env4', label: 'ENV4' },
    { value: 'env5', label: 'ENV5' },
    { value: 'env5noruby', label: 'ENV5-NoRuby' },
    { value: 'env6', label: 'ENV6' },
    { value: 'env7', label: 'ENV7' },
    { value: 'env9', label: 'ENV9' },
    { value: 'env10', label: 'ENV10' },
    { value: 'hadev', label: 'HA Dev' }
  ];

  const userTypes = [
    { value: '1', label: '1 (Provider)' },
    { value: '2', label: '2 (Patient)' }
  ];

  useEffect(() => {
    loadSessions();
    loadEvents();
  }, [environment]);

  const loadSessions = async () => {
    try {
      const response: any = await __request(OpenAPI, {
        method: "GET",
        url: "/api/apitest/p2p/sessions-list/",
        query: { p2pFlag: true }
      });
      setSessions(response.map((session: any) => session.sessionName));
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadEvents = async () => {
    try {
      const response: any = await __request(OpenAPI, {
        method: "GET",
        url: "/api/apitest/p2p/events/",
        query: { 
          environment,
          limit: maxListSize
        }
      });
      setEvents(response);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const getEnvironmentUrl = (env: string, type: 'epic' | 'sof' = 'epic') => {
    const urls: { [key: string]: string } = {
      'stage': 'https://uhehastag.univago.com/',
      'prod': 'https://healthcare.univago.com/',
      'env1': 'https://env1-healthcare.caregility.net/',
      'env2': 'https://env2-healthcare.caregility.net/',
      'env3': 'https://env3-healthcare.caregility.net/',
      'env4': 'https://env4-healthcare.caregility.net/',
      'env5': 'https://env5-healthcare.caregility.net/',
      'env6': 'https://env6-healthcare.caregility.net/',
      'env7': 'https://env7-healthcare.caregility.net/',
      'env9': 'https://env9-healthcare.caregility.net/',
      'env10': 'https://env10-healthcare.caregility.net/',
      'hadev': 'https://uhemobiledev.univago.com/epic-be/epic'
    };

    if (env === 'env5noruby') {
      return type === 'epic' 
        ? 'https://env5-connectapigwepic.caregility.net/'
        : 'https://env5-servicebe-epic.caregility.net/';
    }

    return urls[env] || 'https://en3te1vyaxim1.x.pipedream.net/';
  };

  const getSoFUrl = (env: string) => {
    const urls: { [key: string]: string } = {
      'stage': 'https://stagingservicebe-epic.univago.com/',
      'prod': 'https://servicebe-epic.caregility.net/',
      'env1': 'https://env1-servicebe-epic.caregility.net/',
      'env2': 'https://env2-servicebe-epic.caregility.net/',
      'env3': 'https://env3-servicebe-epic.caregility.net/',
      'env4': 'https://env4-servicebe-epic.caregility.net/',
      'env5': 'https://env5-servicebe-epic.caregility.net/',
      'env6': 'https://env6-servicebe-epic.caregility.net/',
      'env7': 'https://env7-servicebe-epic.caregility.net/',
      'env9': 'https://env9-servicebe-epic.caregility.net/',
      'env10': 'https://env10-servicebe-epic.caregility.net/',
      'hadev': 'https://uhemobiledev.univago.com/epic-be/'
    };

    return urls[env] || 'https://env1-servicebe-epic.caregility.net/';
  };

  const encryptText = async (plainText: string, secret: string): Promise<string> => {
    try {
//      const response = await fetch('http://localhost:8000/encrypt/', {
        const response = await fetch('https://platformapi-testing-be.caregility.net/encrypt/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken') || '',
        },
        body: JSON.stringify({
          text: plainText,
          key: secret
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Encryption failed');
      }

      const data = await response.json();
      return data.encrypted_data;
    } catch (error) {
      console.error('Encryption error:', error);
      throw error;
    }
  };

  const getCookie = (name: string): string => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || '';
    return '';
  };

  const generateLink = async () => {
    if (!apiUserName || !apiEncryptKey || !sessionId || !userType || !displayName) {
      setStatusMessage('Error - at least one of the required parameters is missing!');
      setStatusType('error');
      return;
    }

    if (userType !== '1' && userType !== '2') {
      setStatusMessage('Error - User Type parameter is invalid - must be 1 or 2!');
      setStatusType('error');
      return;
    }

    try {
      setStatusMessage('Generating encrypted link...');
      setStatusType('info');
      
      let url = getEnvironmentUrl(environment);
      
      if (environment.includes('noruby')) {
        url += 'api/meeting?api_username=' + apiUserName.trim() + '&data=';
      } else {
        if (environment === 'hadev') {
          url += '?api_username=' + apiUserName.trim() + '&data=';
        } else {
          url += 'api/meeting?api_username=' + apiUserName.trim() + '&data=';
        }
      }

      let clearText = 'instant=' + Math.floor(Date.now() / 1000);
      clearText += '&sessionid=' + sessionId;
      clearText += '&usertype=' + userType;
      clearText += '&username=' + displayName;

      if (appointmentTime) {
        clearText += '&appttime=' + appointmentTime;
      }

      if (userId) {
        clearText += '&userid=' + userId;
      }

      if (csn) {
        clearText += '&csn=' + csn;
      }

      if (departmentId) {
        clearText += '&dep=' + departmentId;
      }

      const encryptedData = await encryptText(clearText, apiEncryptKey.trim());
      const finalLink = url + encryptedData;

      setGeneratedLink(finalLink);
      setStatusMessage('Link generated successfully!');
      setStatusType('success');
    } catch (error) {
      setStatusMessage('Error generating link: ' + error);
      setStatusType('error');
    }
  };

  const generateSoFLink = () => {
    if (!apiUserName || !sessionId || !userType || !displayName) {
      setStatusMessage('Error - at least one of the required parameters is missing!');
      setStatusType('error');
      return;
    }

    if (userType !== '1' && userType !== '2') {
      setStatusMessage('Error - User Type parameter is invalid - must be 1 or 2!');
      setStatusType('error');
      return;
    }

    try {
      const url = getSoFUrl(environment) + 'fhir';
      const baseUrl = window.location.origin;
      
      // Generate JWT token (simplified - in production, use proper JWT library)
      const token = btoa(JSON.stringify({
        email: 'user@example.com',
        clientId: getClientId(environment, userType),
        environment,
        apiUserName: apiUserName.trim(),
        displayName,
        appointmentTime,
        sessionId,
        userId,
        userType,
        csn,
        departmentId
      }));

      const soFLink = url + '?iss=' + baseUrl + '/api&launch=' + token;
      setGeneratedSoFLink(soFLink);
      setStatusMessage('SMART on FHIR link generated successfully!');
      setStatusType('success');
    } catch (error) {
      setStatusMessage('Error generating SMART on FHIR link: ' + error);
      setStatusType('error');
    }
  };

  const generateDeviceTestLink = () => {
    if (!apiUserName || !sessionId || !userType || !displayName) {
      setStatusMessage('Error - at least one of the required parameters is missing!');
      setStatusType('error');
      return;
    }

    if (userType !== '1' && userType !== '2') {
      setStatusMessage('Error - User Type parameter is invalid - must be 1 or 2!');
      setStatusType('error');
      return;
    }

    try {
      const url = getSoFUrl(environment) + 'device/test';
      const baseUrl = window.location.origin;
      
      // Generate JWT token (simplified - in production, use proper JWT library)
      const token = btoa(JSON.stringify({
        email: 'user@example.com',
        clientId: getClientId(environment, userType),
        environment,
        apiUserName: apiUserName.trim(),
        displayName,
        appointmentTime,
        sessionId,
        userId,
        userType,
        csn,
        departmentId
      }));

      const deviceTestLink = url + '?iss=' + baseUrl + '/api&launch=' + token;
      setGeneratedSoFLink(deviceTestLink);
      setStatusMessage('Device test link generated successfully!');
      setStatusType('success');
    } catch (error) {
      setStatusMessage('Error generating device test link: ' + error);
      setStatusType('error');
    }
  };

  const getClientId = (env: string, userType: string): string => {
    if (userType === '1') { // Provider
      return env === 'prod' 
        ? '30cc2d65-62f3-4f9c-8c64-cd2503fb3cdf'
        : '85590a37-c443-430e-b729-a0f258974c09';
    } else { // Patient
      return env === 'prod'
        ? '6c281298-67e9-418a-acd7-8bd66c17fc15'
        : '22d9c281-ae89-46ad-aec4-2383610913e9';
    }
  };

  const saveSession = async () => {
    if (!sessionName || !apiUserName) {
      setStatusMessage('Error - Session Name and API User Name are required.');
      setStatusType('error');
      return;
    }

    try {
      const session: P2PSession = {
        email: 'user@example.com', // This should come from authentication
        p2pFlag: true,
        environment,
        apiUserName: apiUserName?.trim() || '',
        apiEncryptKey: apiEncryptKey?.trim() || '',
        sessionId: sessionId || '',
        userType: userType || '',
        displayName: displayName || '',
        appointmentTime: appointmentTime || '',
        userId: userId || '',
        csn: csn || '',
        deviceId: departmentId?.trim() || '', // DepartmentId is stored in deviceId field
        poh: false,
        sessionName,
        updated: Date.now()
      };

      await __request(OpenAPI, {
        method: "POST",
        url: "/api/apitest/p2p/sessions-save/",
        body: session
      });

      setStatusMessage('Session saved successfully');
      setStatusType('success');
      await loadSessions();
    } catch (error: any) {
      setStatusMessage('Error saving session: ' + (error.message || error));
      setStatusType('error');
    }
  };

  const deleteSession = async () => {
    if (!sessionName) {
      setStatusMessage('Error - Session Name is required.');
      setStatusType('error');
      return;
    }

    try {
      await __request(OpenAPI, {
        method: "DELETE",
        url: "/api/apitest/p2p/sessions-delete/",
        query: {
          email: 'user@example.com',
          p2pFlag: true,
          sessionName
        }
      });

      setStatusMessage('Session deleted successfully');
      setStatusType('success');
      setSessionName('');
      await loadSessions();
    } catch (error: any) {
      setStatusMessage('Error deleting session: ' + (error.message || error));
      setStatusType('error');
    }
  };

  const loadSession = async (sessionName: string) => {
    if (!sessionName || sessionName === '---') {
      setSessionName('');
      return;
    }

    try {
      console.log('Loading P2P session:', sessionName);
      const response: any = await __request(OpenAPI, {
        method: "GET",
        url: `/api/apitest/p2p/session-detail/`,
        query: {
          email: 'user@example.com',
          p2pFlag: true,
          sessionName: sessionName
        }
      });

      console.log('P2P session response:', response);
      if (response) {
        console.log('Setting form fields with response data:', response);
        setEnvironment(response.environment);
        setApiUserName(response.apiUserName);
        setApiEncryptKey(response.apiEncryptKey);
        setSessionId(response.sessionId);
        setUserType(response.userType);
        setDisplayName(response.displayName);
        setAppointmentTime(response.appointmentTime);
        setUserId(response.userId);
        setCsn(response.csn);
        setDepartmentId(response.deviceId); // DepartmentId is stored in deviceId field
        setSessionName(sessionName);
        console.log('Form fields set successfully');
      }
    } catch (error: any) {
      console.error('Error loading P2P session:', error);
      setStatusMessage('Error loading session: ' + (error.message || error));
      setStatusType('error');
    }
  };



  const openGeneratedLink = () => {
    if (generatedLink) {
      window.open(generatedLink, '_blank');
    }
  };

  const openSoFLink = () => {
    if (generatedSoFLink) {
      window.open(generatedSoFLink, '_blank');
    }
  };

  const refreshEvents = () => {
    loadEvents();
  };

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  return (
    <Container fluid className="p2p-container">
      <Row>
        <Col>
          <h1>P2P Call</h1>
          <p>
            Choose environment and provide required parameters (displayed in <strong>bold</strong>). 
            Default: Staging - "Caregility Test" customer.
            <br />
            Use buttons below to create links to share, or make calls. (Sessions can be saved for future use.)
          </p>

          <Row className="main-content-flex">
            {/* Left Sidebar */}
            <div className={`sidebar left ${sidebarOpen ? "open" : "closed"}`}>
              <Button variant="outline-primary" size="sm" onClick={toggleSidebar} className="toggle-btn">
                {sidebarOpen ? "←" : "→"}
              </Button>

              {sidebarOpen && (
                <>
                  <h4>Saved Sessions</h4>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Saved Sessions:</strong></Form.Label>
                    <Form.Select 
                      value={sessionName} 
                      onChange={(e) => loadSession(e.target.value)}
                    >
                      <option value="---">---</option>
                      {sessions.map((session, index) => (
                        <option key={index} value={session}>{session}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <h4>Session Management</h4>
                  <Form.Group className="mb-3">
                    <Form.Label>Session Name:</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={sessionName} 
                      onChange={(e) => setSessionName(e.target.value)}
                      placeholder="Enter session name"
                    />
                  </Form.Group>
                  <div className="d-grid gap-2">
                    <Button variant="success" size="sm" onClick={saveSession}>
                      Save Session
                    </Button>
                    <Button variant="danger" size="sm" onClick={deleteSession}>
                      Delete Session
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Center Content */}
            <div className="center-content">
              <h4>Actions</h4>
              
              <Row>
                <Col md={6}>
                  <h6><strong>P2P Calls:</strong></h6>
                  <div className="d-grid gap-2">
                    <Button variant="primary" onClick={generateLink}>
                      Generate Link
                    </Button>
                    <Button variant="primary" onClick={openGeneratedLink} disabled={!generatedLink}>
                      Encrypted API Call
                    </Button>
                    <Button variant="secondary" onClick={generateSoFLink}>
                      SMART on FHIR
                    </Button>
                    <Button variant="secondary" onClick={generateDeviceTestLink}>
                      Device Test
                    </Button>
                  </div>
                </Col>
                <Col md={6}>
                  <h6><strong>Event History:</strong></h6>
                  <div className="d-grid gap-2">
                    <Button variant="info" onClick={refreshEvents}>
                      Refresh Events
                    </Button>
                    <Button variant="info" onClick={openSoFLink} disabled={!generatedSoFLink}>
                      Open Generated Link
                    </Button>
                  </div>
                </Col>
              </Row>

              {statusMessage && (
                <Alert variant={statusType} className="mb-4">
                  <strong>Status:</strong> {statusMessage}
                </Alert>
              )}

              {generatedLink && (
                <Card className="mb-4">
                  <Card.Header>
                    <h5>Generated Link</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Control 
                      as="textarea" 
                      rows={3} 
                      value={generatedLink} 
                      readOnly 
                    />
                  </Card.Body>
                </Card>
              )}

              <h4>Configuration</h4>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Environment:</strong></Form.Label>
                    <Form.Select value={environment} onChange={(e) => setEnvironment(e.target.value)}>
                      {environments.map((env) => (
                        <option key={env.value} value={env.value}>{env.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>API User Name:</strong> [From admin portal]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={apiUserName} 
                      onChange={(e) => setApiUserName(e.target.value)}
                      placeholder="API User Name"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>API Secret:</strong> [From admin portal - use "API Encrypt Key"]</Form.Label>
                    <Form.Control 
                      type="password" 
                      value={apiEncryptKey} 
                      onChange={(e) => setApiEncryptKey(e.target.value)}
                      placeholder="API Encrypt Key"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Session ID:</strong> [Unique session identifier, e.g. 122333444455555]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={sessionId} 
                      onChange={(e) => setSessionId(e.target.value)}
                      placeholder="Session ID"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>User Type:</strong> [1 (for provider) or 2 (for patient)]</Form.Label>
                    <Form.Select value={userType} onChange={(e) => setUserType(e.target.value)}>
                      {userTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Display Name:</strong> [Name of the provider or patient, e.g. Dr. John Doe]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={displayName} 
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Display Name"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Appointment Time: [e.g. 10/21/2022 10:40 AM EST]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={appointmentTime} 
                      onChange={(e) => setAppointmentTime(e.target.value)}
                      placeholder="Appointment Time"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>User ID: [e.g. 123456]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={userId} 
                      onChange={(e) => setUserId(e.target.value)}
                      placeholder="User ID"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>CSN: [Contact Serial Number - used for group visits, e.g. 123412345678]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={csn} 
                      onChange={(e) => setCsn(e.target.value)}
                      placeholder="CSN"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Department ID: [e.g. XYZ-123]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={departmentId} 
                      onChange={(e) => setDepartmentId(e.target.value)}
                      placeholder="Department ID"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <h4>Event History</h4>
              
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span>Showing max {maxListSize} events for {environment}</span>
                <Button variant="primary" size="sm" onClick={refreshEvents}>
                  Refresh Table
                </Button>
              </div>

              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Time (UTC)</th>
                    <th>Event</th>
                    <th>Session ID</th>
                    <th>User Type</th>
                    <th>User ID</th>
                    <th>Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td>{new Date(event.eventTime).toLocaleString()}</td>
                      <td>{event.eventType}</td>
                      <td>{event.sessionId}</td>
                      <td>{event.userType}</td>
                      <td>{event.userId}</td>
                      <td>{event.customer}</td>
                    </tr>
                  ))}
                  {events.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        No events found for this environment
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default P2PTesting;
